import { i18n } from "../plugin/vue-i18n";
import { DUPLICATE_ALLOWED_FIELDS } from "./constants";

interface Slide {
  slideName: string;
  slideData: Array<{
    uuid?: string;
    [key: string]: any;
  }>;
  id: string;
  slideNumber: number;
  active?: boolean;
}

interface Field {
  type?: string;
  [key: string]: any;
}

export const createSlide = (slideLength) => {
  return {
    slideName: getSuccessorIndex(slideLength),
    slideData: [],
    id: new Date().getTime() + "-" + slideLength,
    slideNumber: slideLength + 1,
  };
};

const getSuccessorIndex = (slideLength) => {
  // Localized default slide label
  return i18n.global.t("survey.slideLabel", { n: slideLength + 1 }) as string;
};

// Creates a new slide with the given fields
const createNewSlide = (fields: Field[], slideIndex: number): Slide => ({
  ...createSlide(slideIndex),
  active: false,
  slideData: fields,
});

// Handles adding a source field to the last slide
const handleSourceField = (field: Field, commonFields: Field[], newSlides: Slide[]): void => {
  const slideFields = commonFields.length ? [...commonFields, field] : [field];
  if (newSlides.length > 0) {
    newSlides[newSlides.length - 1].slideData.push(...slideFields);
  }
};

// Handles creating a new slide for other type fields
const handleOtherTypeField = (field: Field, commonFields: Field[], newSlides: Slide[]): void => {
  const slideFields = commonFields.length ? [...commonFields, field] : [field];
  newSlides.push(createNewSlide(slideFields, newSlides.length));
};

// Determines if a field should be handled as a source field
const isSourceField = (field: Field): boolean => field.type === "source";

// Determines if the current field is the last one in the array
const isLastField = (index: number, totalLength: number): boolean => index === totalLength - 1;

// Determines if a field should be added to common fields based on version and type
const shouldAddToCommonFields = (field: Field, isNewVersion: boolean): boolean =>
  isSourceField(field) || (!isNewVersion && DUPLICATE_ALLOWED_FIELDS.includes(field.type));

// Determines if a field should create a new slide
const shouldCreateNewSlide = (
  field: Field,
  index: number,
  totalLength: number,
  isNewVersion: boolean
): boolean => isLastField(index, totalLength) || !shouldAddToCommonFields(field, isNewVersion);

/**
 * Splits slide data into separate slides, with one field per slide
 * @param slide - The slide containing fields to split
 * @param isNewVersion - Whether to use new version logic for handling fields
 * @returns Array of slides with split fields
 */
export const splitOneFieldPerSlide = (slide: Slide, isNewVersion = false): Slide[] => {
  if (!slide?.slideData?.length) {
    return [createSlide(0)];
  }

  const newSlides: Slide[] = [];
  let commonFieldsForSlide: Field[] = [];

  slide.slideData.forEach((field, index) => {
    if (shouldCreateNewSlide(field, index, slide.slideData.length, isNewVersion)) {
      if (isSourceField(field)) {
        handleSourceField(field, commonFieldsForSlide, newSlides);
      } else {
        handleOtherTypeField(field, commonFieldsForSlide, newSlides);
      }
      commonFieldsForSlide = [];
    } else {
      commonFieldsForSlide.push(field);
    }
  });

  return newSlides;
};

export const mergeAllFieldsInSlide = (slides) => {
  const mappedSlideFromOtherElements = [];
  slides.forEach((slide) => {
    slide.slideData.forEach((element) => {
      mappedSlideFromOtherElements.push(element);
    });
  });
  return { ...createSlide(0), active: false, slideData: mappedSlideFromOtherElements };
};

export const transformOneQuestionLogicForV2 = (slides, slideLogic) => {
  const logic = slideLogic || {};
  const fieldSlideMap = getMapOfFieldToSlide(slides, logic);
  const transFormedSlides = slides.map((slide) => {
    slide.slideData.forEach((field) => {
      if (fieldSlideMap[field.tag]) {
        slide.logic = { [field.tag]: fieldSlideMap[field.tag] };
      }
    });
    return slide;
  });
  return transFormedSlides;
};

const getMapOfFieldToSlide = (slides, slideLogic) => {
  Object.keys(slideLogic).forEach((fieldTag) => {
    Object.keys(slideLogic[fieldTag]).forEach((option) => {
      const logicTag = slideLogic[fieldTag][option].id;
      if (["disqualify", "endsurvey"].includes(logicTag)) {
        slideLogic[fieldTag][option].id = logicTag;
        slideLogic[fieldTag][option].name =
          logicTag === "disqualify" ? "Disqualify immediately" : "Disqualify after submit";
        return slideLogic;
      }
      const slideInfo = slides.find((slide) =>
        slide.slideData.find((field) => field.tag === logicTag)
      );
      //If slideInfo is not found, which means custom field got deleted. removing logic
      if (slideInfo) {
        slideLogic[fieldTag][option].id = slideInfo.id;
        slideLogic[fieldTag][option].name = `P${slideInfo.slideNumber} - ${slideInfo.slideName}`;
      } else {
        delete slideLogic[fieldTag][option];
        if (Object.keys(slideLogic[fieldTag]).length === 0) {
          delete slideLogic[fieldTag];
        }
      }
    });
  });
  return slideLogic;
};

export const findLastElement = (fields) => {
  return fields[fields.length - 1];
};

/**
 * Finds a matching field using either uuid or tag
 * @param field - The field to match against
 * @param oneSlide - The slide containing fields to search
 * @returns The index of the matching field, or -1 if not found
 */
const findMatchingFieldIndex = (field: any, oneSlide: Slide): number => {
  if (!field) {
    return -1;
  }

  return oneSlide.slideData.findIndex((slideField) => {
    return field.uuid && slideField.uuid === field.uuid;
  });
};

/**
 * Converts slides from one-question-per-slide state back to original state
 * @param originalSlides - Array of slides in original format
 * @param oneQuestionSlides - Array of slides in one-question-per-slide format
 * @returns Array of slides in original format
 */
export const convertToOffState = (
  originalSlides: Slide[] | null,
  oneQuestionSlides: Slide[]
): Slide[] => {
  // If no original slides, merge all one-question slides into a single slide
  if (!originalSlides) {
    return [mergeAllFieldsInSlide(oneQuestionSlides)];
  }

  // Process each original slide
  const processedSlides = originalSlides.map((originalSlide) => {
    try {
      const lastField = findLastElement(originalSlide.slideData);
      if (!lastField) {
        console.warn("Last field not found in slide data");
        return originalSlide;
      }
      // Find both the slide index and field index where the last field appears
      let matchingSlideIndex = -1;
      let matchingFieldIndex = -1;

      oneQuestionSlides.some((oneSlide, slideIndex) => {
        const fieldIndex = findMatchingFieldIndex(lastField, oneSlide);

        if (fieldIndex !== -1) {
          matchingSlideIndex = slideIndex;
          matchingFieldIndex = fieldIndex;
          return true; // Break the some() loop
        }
        return false;
      });
      if (matchingSlideIndex === -1 || matchingFieldIndex === -1) {
        // If no match found, combine all remaining fields into this slide
        const remainingFields = oneQuestionSlides.flatMap((slide) => slide.slideData);
        oneQuestionSlides.length = 0; // Clear oneQuestionSlides
        return {
          ...originalSlide,
          slideData: remainingFields,
        };
      }

      // Collect all fields from slides up to the matching slide
      // and include fields up to the matching field index in the last slide
      const combinedFields = oneQuestionSlides
        .slice(0, matchingSlideIndex)
        .flatMap((slide) => slide.slideData);

      // Add fields from the matching slide up to the matching field index
      if (matchingSlideIndex >= 0 && oneQuestionSlides[matchingSlideIndex]) {
        const matchingSlideFields = oneQuestionSlides[matchingSlideIndex].slideData.slice(
          0,
          matchingFieldIndex + 1
        );
        combinedFields.push(...matchingSlideFields);
      }
      // Remove processed slides from oneQuestionSlides
      oneQuestionSlides.splice(0, matchingSlideIndex + 1);

      // Return updated slide with combined fields
      return {
        ...originalSlide,
        slideData: combinedFields,
      };
    } catch (error) {
      console.error("Error processing slide:", error);
      return originalSlide;
    }
  });

  // Check if there are any remaining fields in oneQuestionSlides
  if (oneQuestionSlides.length > 0) {
    const remainingFields = oneQuestionSlides.flatMap((slide) => slide.slideData);
    if (remainingFields.length > 0) {
      // Create a new slide with remaining fields
      const newSlide = {
        ...createSlide(processedSlides.length),
        slideData: remainingFields,
        active: false,
      };
      processedSlides.push(newSlide);
    }
  }

  return processedSlides;
};

export const updateOptionsWithFocusPreservation = (
  newOptions: any[],
  currentOptions: any[],
  defaultOption: any,
  activeInputIdRef: any,
  activeInputPositionRef: any
) => {
  if (!newOptions || !Array.isArray(newOptions)) {
    newOptions = [defaultOption];
  }

  const activeElement = document.activeElement as HTMLInputElement;
  if (activeElement?.id) {
    activeInputIdRef.value = activeElement.id;
    activeInputPositionRef.value = activeElement.selectionStart;
  }

  const processedOptions =
    Array.isArray(newOptions) && typeof newOptions[0] === "string"
      ? newOptions.map((option) => ({ label: option, calculatedValue: "" }))
      : [...newOptions];

  if (activeInputIdRef.value) {
    setTimeout(() => {
      const element = document.getElementById(activeInputIdRef.value) as HTMLInputElement;
      if (element) {
        element.focus();
        if (activeInputPositionRef.value !== null) {
          element.selectionStart = activeInputPositionRef.value;
          element.selectionEnd = activeInputPositionRef.value;
        }
      }
    }, 0);
  }

  return processedOptions;
};
